import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Mail, Shield, UserPlus, Trash2, X, Info, ShieldCheck, ChevronDown, UserCircle, Key, MoreVertical, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import adminService from '../../services/adminService';

const SharedMailboxManagement = () => {
    const [mailboxes, setMailboxes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    
    // UI Panels logic
    const [isProvisioningOpen, setIsProvisioningOpen] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null); // stores mailbox.id
    
    const [newMailbox, setNewMailbox] = useState({ name: '', email: '' });
    const [isProvisioning, setIsProvisioning] = useState(false);
    
    const [permissions, setPermissions] = useState([]);
    const [newPermission, setNewPermission] = useState({ userEmail: '', permission: 'FULL_ACCESS' });
    const [isAddingPerm, setIsAddingPerm] = useState(false);

    useEffect(() => {
        fetchMailboxes();
    }, []);

    const fetchMailboxes = async () => {
        setIsLoading(true);
        try {
            const data = await adminService.getSharedMailboxes();
            setMailboxes(data);
        } catch (error) {
            console.error('Failed to fetch shared mailboxes', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateMailbox = async (e) => {
        e.preventDefault();
        setIsProvisioning(true);
        try {
            await adminService.createSharedMailbox(newMailbox);
            setNewMailbox({ name: '', email: '' });
            setIsProvisioningOpen(false);
            fetchMailboxes();
        } catch (error) {
            console.error('Failed to create shared mailbox', error);
            const msg = error.response?.data?.message || 'Failed to provision mailbox.';
            alert(msg);
        } finally {
            setIsProvisioning(false);
        }
    };

    const handleDeleteMailbox = async (id) => {
        if (!window.confirm('Are you certain you want to purge this shared identifier? This action is irreversible.')) return;
        try {
            await adminService.deleteSharedMailbox(id);
            fetchMailboxes();
            if (expandedRow === id) setExpandedRow(null);
        } catch (error) {
            console.error('Failed to delete shared mailbox', error);
        }
    };

    const togglePermissions = async (mailbox) => {
        if (expandedRow === mailbox.id) {
            setExpandedRow(null);
            setPermissions([]);
            return;
        }

        setExpandedRow(mailbox.id);
        setIsProvisioningOpen(false); // Close provisioning when opening permissions
        try {
            const data = await adminService.getSharedPermissions(mailbox.id);
            setPermissions(data);
        } catch (error) {
            console.error('Failed to fetch permissions', error);
        }
    };

    const handleAddPermission = async (e) => {
        e.preventDefault();
        if (!expandedRow) return;
        setIsAddingPerm(true);
        try {
            await adminService.addSharedPermission(expandedRow, newPermission);
            setNewPermission({ userEmail: '', permission: 'FULL_ACCESS' });
            // Refresh permissions
            const data = await adminService.getSharedPermissions(expandedRow);
            setPermissions(data);
        } catch (error) {
            console.error('Failed to add permission', error);
            const msg = error.response?.data?.message || 'Failed to grant access.';
            alert(msg);
        } finally {
            setIsAddingPerm(false);
        }
    };

    const handleRemovePermission = async (permId) => {
        try {
            await adminService.removeSharedPermission(permId);
            // Refresh permissions
            const data = await adminService.getSharedPermissions(expandedRow);
            setPermissions(data);
        } catch (error) {
            console.error('Failed to remove permission', error);
        }
    };

    const filteredMailboxes = mailboxes.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ─── Inline Panel Renderers ────────────────────────────────────────

    const renderProvisioningPanel = () => {
        return (
            <div className="p-8 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/30 border-b border-[rgb(var(--border))] animate-fade-in">
                <div className="flex flex-col md:flex-row gap-8 max-w-5xl mx-auto">
                    {/* Header Info */}
                    <div className="w-full md:w-1/3 space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                <UserPlus size={20} />
                            </div>
                            <div>
                                <h3 className="text-sm font-black uppercase tracking-[0.2em] text-slate-800 dark:text-white leading-tight">Provision Identity</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Infrastructure Layer</p>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed">
                            Provisioning a new shared identifier creates a locked virtual account in the MTA registry. Access must be granted to existing identities manually.
                        </p>
                        <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10">
                            <div className="flex items-center gap-2 mb-1">
                                <Shield size={12} className="text-amber-600" />
                                <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Policy Note</span>
                            </div>
                            <p className="text-[10px] font-bold text-amber-800/60 dark:text-amber-200/40 leading-snug uppercase tracking-tight">
                                Requires valid @xevyte domain scope for auto-routing.
                            </p>
                        </div>
                    </div>

                    {/* Form Component */}
                    <div className="flex-1">
                        <form onSubmit={handleCreateMailbox} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Display Context Name</label>
                                    <div className="relative group">
                                        <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={16} />
                                        <input
                                            required
                                            className="premium-input w-full !pl-12 !py-3.5 text-sm font-bold shadow-sm"
                                            placeholder="e.g. Sales Division"
                                            value={newMailbox.name}
                                            onChange={(e) => setNewMailbox({ ...newMailbox, name: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Primary Route Address</label>
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={16} />
                                        <input
                                            required
                                            type="email"
                                            className="premium-input w-full !pl-12 !py-3.5 text-sm font-bold shadow-sm"
                                            placeholder="sales@xevyte.co"
                                            value={newMailbox.email}
                                            onChange={(e) => setNewMailbox({ ...newMailbox, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsProvisioningOpen(false)}
                                    className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-200 transition-all"
                                >
                                    Discard Changes
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isProvisioning}
                                    className="premium-button-primary px-10 py-3.5 rounded-2xl flex items-center gap-3 shadow-xl shadow-indigo-600/20"
                                >
                                    {isProvisioning ? (
                                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Syncing Registry...</>
                                    ) : (
                                        <><Plus size={18} />Complete Provisioning</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    };

    const renderPermissionsPanel = (mailbox) => {
        return (
            <div className="p-8 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/30">
                <div className="flex flex-col md:flex-row gap-8 max-w-5xl mx-auto">
                    {/* Add Member Column */}
                    <div className="w-full md:w-1/3 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                <UserPlus size={16} />
                            </div>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-800 dark:text-slate-200">Authorize Member</h4>
                        </div>
                        <form onSubmit={handleAddPermission} className="space-y-3">
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Target User Email</label>
                                <input
                                    required
                                    type="email"
                                    className="premium-input w-full text-xs font-bold !py-2.5 shadow-sm"
                                    placeholder="user@xevyte.co"
                                    value={newPermission.userEmail}
                                    onChange={(e) => setNewPermission({ ...newPermission, userEmail: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Access Level</label>
                                <select
                                    className="premium-input w-full text-xs font-black uppercase tracking-tight !py-2.5 shadow-sm overflow-hidden"
                                    value={newPermission.permission}
                                    onChange={(e) => setNewPermission({ ...newPermission, permission: e.target.value })}
                                >
                                    <option value="FULL_ACCESS">Full Access</option>
                                    <option value="READ_ONLY">Read Only</option>
                                </select>
                            </div>
                            <button 
                                type="submit" 
                                disabled={isAddingPerm}
                                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-indigo-600/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isAddingPerm ? 'Authorizing...' : 'Grant Access'}
                            </button>
                        </form>
                    </div>

                    {/* Members List Column */}
                    <div className="flex-1 space-y-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                                    <ShieldCheck size={16} />
                                </div>
                                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-800 dark:text-slate-200">Authorized Delegates</h4>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{permissions.length} Active Users</span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {permissions.map(p => (
                                <div key={p.id} className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-[rgb(var(--border))] group hover:border-indigo-500/30 transition-all flex items-center justify-between shadow-sm animate-fade-in">
                                    <div className="flex items-center space-x-3 truncate">
                                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-indigo-500/10 group-hover:text-indigo-500 transition-colors flex-shrink-0">
                                            <UserCircle size={16} />
                                        </div>
                                        <div className="truncate">
                                            <p className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-tighter truncate font-mono">{p.userEmail}</p>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <Key size={8} className="text-[#4f46e5]" />
                                                <span className="text-[9px] font-bold text-[#4f46e5] uppercase tracking-widest">{p.permission.replace('_', ' ')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemovePermission(p.id)}
                                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0"
                                        title="Revoke Permission"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                            {permissions.length === 0 && (
                                <div className="col-span-2 py-8 text-center bg-slate-50/50 dark:bg-slate-900/50 rounded-2xl border border-dashed border-[rgb(var(--border))]">
                                    <Users size={24} className="mx-auto text-slate-300 mb-2" />
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">No authorized members yet</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Close button for inline panel */}
                    <div className="flex-shrink-0">
                        <button onClick={() => setExpandedRow(null)} className="p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all">
                            <X size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">
                        Shared Mailboxes
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">Manage collaborative identifiers and access permissions.</p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="flex items-center text-[#4f46e5] bg-[#4f46e5]/10 px-4 py-2 rounded-2xl text-[10px] font-bold uppercase tracking-[0.1em] border border-[#4f46e5]/20 shadow-sm">
                        <ShieldCheck size={14} className="mr-2 text-emerald-500" />
                        Infrastructure Secure
                    </div>
                    <button
                        onClick={() => {
                            setIsProvisioningOpen(!isProvisioningOpen);
                            setExpandedRow(null);
                        }}
                        className={`px-6 py-2.5 flex items-center gap-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isProvisioningOpen ? 'bg-slate-900 text-white shadow-xl' : 'premium-button-primary bg-[#4f46e5] text-white shadow-lg shadow-[#4f46e5]/25'}`}
                    >
                        {isProvisioningOpen ? <X size={18} /> : <Plus size={18} />}
                        {isProvisioningOpen ? 'Cancel' : 'Provision Account'}
                    </button>
                </div>
            </header>

            <div className="enterprise-card overflow-hidden !border-slate-200/60 dark:!border-slate-800/60 shadow-xl">
                {/* Conditionally reveal provisioning panel at the top of the table area */}
                {isProvisioningOpen && renderProvisioningPanel()}

                <div className="p-6 border-b border-[rgb(var(--border))] flex items-center justify-between glass">
                    <div className="flex items-center space-x-4 flex-1 max-w-md">
                        <div className="premium-input flex items-center shadow-sm w-full">
                            <Search size={16} className="text-slate-400 mr-3" />
                            <input
                                type="text"
                                placeholder="Search by identifier or label..."
                                className="bg-transparent border-none p-0 focus:ring-0 text-base w-full placeholder:text-slate-400 font-medium"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 dark:bg-slate-900/50 px-4 py-2 rounded-xl border border-[rgb(var(--border))] shadow-inner">
                        <Users size={14} className="text-[#4f46e5]" />
                        <span>{mailboxes.length} Total Identifiers</span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-[rgb(var(--border))]">
                            <tr>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Mailbox Identifier</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Routing Strategy</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Status</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Registry Date</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] text-right">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[rgb(var(--border))]">
                            {filteredMailboxes.map((mailbox) => {
                                const isExpanded = expandedRow === mailbox.id;
                                
                                return (
                                    <React.Fragment key={mailbox.id}>
                                        <tr className={`group transition-all ${isExpanded ? 'bg-[#4f46e5]/[0.03] border-l-4 border-l-[#4f46e5]' : 'hover:bg-slate-50/50 dark:hover:bg-[#4f46e5]/5'}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-4">
                                                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all border border-[rgb(var(--border))] shadow-sm ${isExpanded ? 'bg-[#4f46e5]/10 text-[#4f46e5]' : 'bg-white dark:bg-slate-800 text-slate-500 group-hover:text-[#4f46e5] group-hover:bg-[#4f46e5]/10'}`}>
                                                        <Users size={22} />
                                                    </div>
                                                    <div>
                                                        <p className="font-black text-slate-900 dark:text-slate-100 leading-tight uppercase text-sm tracking-tight">{mailbox.name}</p>
                                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1 italic">Shared Identity Context</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <Mail size={12} className="text-[#4f46e5]" />
                                                    <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-300 uppercase tracking-tighter italic">{mailbox.email}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shadow-sm">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 shadow-[0_0_8px_rgba(16,185,129,0.5)] anim-pulse"></span>
                                                    Active
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-widest">Provisioned</span>
                                                    <span className="text-[10px] font-medium text-slate-400 uppercase mt-0.5">{mailbox.createdAt ? new Date(mailbox.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => togglePermissions(mailbox)}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'border-indigo-100 dark:border-indigo-900/30 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-[#4f46e5]/10'}`}
                                                        title="Manage Authorizations"
                                                    >
                                                        {isExpanded ? <MoreVertical size={18} /> : <Shield size={18} />}
                                                    </button>
                                                    <button
                                                        className="p-2.5 rounded-xl border border-indigo-100 dark:border-indigo-900/30 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-[#4f46e5]/10 transition-all shadow-sm"
                                                        title="Mailbox Analytics"
                                                    >
                                                        <Info size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMailbox(mailbox.id)}
                                                        className="p-2.5 rounded-xl border border-red-100 dark:border-red-900/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all shadow-sm"
                                                        title="Purge Identifier"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan="5" className="p-0 border-l-4 border-l-[#4f46e5] border-b-2 border-b-[#4f46e5]/10">
                                                    <div className="animate-fade-in shadow-inner">
                                                        {renderPermissionsPanel(mailbox)}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            
                            {filteredMailboxes.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan="5" className="px-10 py-16 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-2xl flex items-center justify-center text-slate-300 mb-4 border border-[rgb(var(--border))]">
                                                <Users size={32} />
                                            </div>
                                            <p className="text-sm font-black text-slate-400 uppercase tracking-widest">No shared identifiers found</p>
                                            <p className="text-xs text-slate-300 mt-2">Create your first collaborative account above</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SharedMailboxManagement;
